import React, { useEffect, useState } from 'react';
import { useStreak } from '../../hooks/useStreak';
import { ViewWrapper } from '../ViewWrapper/ViewWrapper';

interface StreakElementProps {
  streakCount?: number; // Keep for backward compatibility
}

const StreakElement: React.FC<StreakElementProps> = ({ streakCount: propStreakCount }) => {
  const { streakData, loading, error } = useStreak();
  const [showTooltip, setShowTooltip] = useState(false);
  const [previousStreak, setPreviousStreak] = useState<number | null>(null);
  const [isStreakIncreasing, setIsStreakIncreasing] = useState(false);

  // Use real streak data if available, fallback to prop, then default
  const displayStreakCount = streakData?.currentStreak || propStreakCount || 0;

  // Track streak changes for animation
  useEffect(() => {
    if (streakData?.currentStreak && previousStreak !== null) {
      if (streakData.currentStreak > previousStreak) {
        setIsStreakIncreasing(true);
        setTimeout(() => setIsStreakIncreasing(false), 2000);
      }
    }
    setPreviousStreak(streakData?.currentStreak || null);
  }, [streakData?.currentStreak, previousStreak]);

  const handleStreakClick = () => {
    if (streakData) {
      setShowTooltip(!showTooltip);
    }
  };

  const getStreakDisplay = () => {
    if (loading) return '...';
    if (error) return '?';
    return displayStreakCount;
  };

  const getStreakCounterClass = (isTodayActivate: any) => {
    let className = isTodayActivate ? 'streak-counter-activate' : 'streak-counter';
    if (streakData) className += ' clickable';
    if (loading) className += ' loading';
    if (error) className += ' error';
    if (isStreakIncreasing) className += ' increasing';
    return className;
  };

  return (
    <ViewWrapper disable={loading} dontRemove>
      <div
        className={getStreakCounterClass(streakData?.totalDaysActive)}
        onClick={handleStreakClick}
        onMouseEnter={() => streakData && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        title={error ? 'Failed to load streak data' : 'Click to see streak details'}
      >
        <span className="streak-number">
          {getStreakDisplay()}
          {isStreakIncreasing && <span className="streak-increase">+1</span>}
        </span>
        {streakData?.totalDaysActive ? (
          <img src="/images/ui/flame-fill.png" alt="Streak" className="streak-icon" />
        ) : (
          <img src="/images/ui/flame.png" alt="Streak" className="streak-icon" />
        )}

        {showTooltip && streakData && (
          <div className="streak-tooltip">
            <div className="tooltip-content">
              <div className="tooltip-row">
                <span>🔥 Current:</span>
                <span className="tooltip-value">{streakData.currentStreak} days</span>
              </div>
              {streakData.longestStreak > streakData.currentStreak && (
                <div className="tooltip-row">
                  <span>🏆 Longest:</span>
                  <span className="tooltip-value">{streakData.longestStreak} days</span>
                </div>
              )}
              <div className="tooltip-row">
                <span>📊 Total:</span>
                <span className="tooltip-value">{streakData.totalDaysActive} days</span>
              </div>
              {streakData.isActiveToday && (
                <div className="tooltip-row success">
                  <span>✅ Active today!</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </ViewWrapper>
  );
};

export default StreakElement;
